// client/src/utils/notifications.js
import toastr from 'toastr'

/**
 * Centralized notification utilities for UI layer
 * Separated from services following Single Responsibility Principle
 *
 * Includes rate limiting to prevent notification spam
 */

/**
 * Notification queue for rate limiting
 * Tracks recent notifications to prevent duplicates and spam
 */
const notificationQueue = {
  recentMessages: new Map(), // Map<messageHash, timestamp>
  DUPLICATE_WINDOW: 2000, // 2 seconds - ignore duplicate messages within this window
  RATE_LIMIT_WINDOW: 5000, // 5 seconds - track rate limiting window
  MAX_PER_WINDOW: 5, // Maximum notifications per rate limit window
  messageTimestamps: [], // Array of timestamps for rate limiting

  /**
   * Check if a message should be shown based on rate limiting rules
   * @param {string} message - The message to check
   * @param {string} type - Notification type (success, error, etc.)
   * @returns {boolean} - True if message should be shown
   */
  shouldShow(message, type) {
    const now = Date.now()
    const messageHash = `${type}:${message}`

    // Check for duplicate message within duplicate window
    if (this.recentMessages.has(messageHash)) {
      const lastShown = this.recentMessages.get(messageHash)
      if (now - lastShown < this.DUPLICATE_WINDOW) {
        return false // Suppress duplicate
      }
    }

    // Check rate limiting - remove old timestamps
    this.messageTimestamps = this.messageTimestamps.filter(
      (ts) => now - ts < this.RATE_LIMIT_WINDOW
    )

    // Check if we've hit the rate limit
    if (this.messageTimestamps.length >= this.MAX_PER_WINDOW) {
      // Show a warning instead if not already shown recently
      if (
        !this.recentMessages.has('rate-limit-warning') ||
        now - this.recentMessages.get('rate-limit-warning') >
          this.RATE_LIMIT_WINDOW
      ) {
        toastr.warning('Too many notifications - some have been suppressed')
        this.recentMessages.set('rate-limit-warning', now)
      }
      return false
    }

    // Allow the message
    this.recentMessages.set(messageHash, now)
    this.messageTimestamps.push(now)

    // Clean up old entries from recentMessages
    for (const [hash, timestamp] of this.recentMessages.entries()) {
      if (
        now - timestamp > this.DUPLICATE_WINDOW &&
        hash !== 'rate-limit-warning'
      ) {
        this.recentMessages.delete(hash)
      }
    }

    return true
  },

  /**
   * Reset the notification queue (useful for testing)
   */
  reset() {
    this.recentMessages.clear()
    this.messageTimestamps = []
  },
}

/**
 * Show success notification with rate limiting
 * @param {string} message - Success message to display
 */
export function showSuccess(message) {
  if (notificationQueue.shouldShow(message, 'success')) {
    toastr.success(message)
  }
}

/**
 * Show error notification with rate limiting
 * @param {string} message - Error message to display
 */
export function showError(message) {
  if (notificationQueue.shouldShow(message, 'error')) {
    toastr.error(message)
  }
}

/**
 * Show info notification with rate limiting
 * @param {string} message - Info message to display
 */
export function showInfo(message) {
  if (notificationQueue.shouldShow(message, 'info')) {
    toastr.info(message)
  }
}

/**
 * Show warning notification with rate limiting
 * @param {string} message - Warning message to display
 */
export function showWarning(message) {
  if (notificationQueue.shouldShow(message, 'warning')) {
    toastr.warning(message)
  }
}

/**
 * Handle service response with automatic notifications
 *
 * Usage Guidelines:
 * - Use for SIMPLE operations where you just need to show a notification
 * - NOT suitable when you need to perform additional actions on success
 * - For complex flows (like modals, navigation), use explicit if (result.success) checks
 *
 * @param {Object} response - Service response object from a service method
 * @param {string} successMessage - Message to show on success (optional)
 * @returns {Object} - The original response object (for chaining)
 *
 * @example
 * // Simple usage - just show notification
 * const result = handleServiceResponse(
 *   await services.item.delete(id),
 *   'Item deleted successfully'
 * )
 *
 * // Don't use when you need to do more on success
 * // Instead use explicit checks:
 * const result = await services.item.create(data)
 * if (result.success) {
 *   showSuccess('Item created!')
 *   navigate('/items')  // Additional action
 * }
 */
export function handleServiceResponse(response, successMessage = null) {
  if (response.success) {
    if (successMessage) {
      showSuccess(successMessage)
    }
  } else if (response.error) {
    showError(response.error)
  }
  return response
}

/**
 * Unified notifications object following the services pattern
 */
const notifications = {
  showSuccess,
  showError,
  showInfo,
  showWarning,
  handleServiceResponse,
  resetQueue: () => notificationQueue.reset(), // For testing
}

export default notifications
